import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";

/* ═══════════════════════════════════════════════════════
   STEAM PIPE SIZING CALCULATOR
   Size steam supply & condensate return lines based on
   pressure, load (lbs/hr), and max velocity.
   ═══════════════════════════════════════════════════════ */

interface SteamPipeEntry {
  nominal: string;
  id: number; // internal diameter inches
}

const STEAM_PIPE_SIZES: SteamPipeEntry[] = [
  { nominal: '3/4"', id: 0.824 },
  { nominal: '1"', id: 1.049 },
  { nominal: '1-1/4"', id: 1.380 },
  { nominal: '1-1/2"', id: 1.610 },
  { nominal: '2"', id: 2.067 },
  { nominal: '2-1/2"', id: 2.469 },
  { nominal: '3"', id: 3.068 },
  { nominal: '4"', id: 4.026 },
  { nominal: '5"', id: 5.047 },
  { nominal: '6"', id: 6.065 },
  { nominal: '8"', id: 7.981 },
  { nominal: '10"', id: 10.020 },
  { nominal: '12"', id: 12.000 },
];

// Approximate specific volume of saturated steam (ft³/lb) by pressure
const STEAM_PRESSURES: { label: string; psig: number; specificVol: number }[] = [
  { label: "0 psig (Atmos)", psig: 0, specificVol: 26.80 },
  { label: "2 psig", psig: 2, specificVol: 22.17 },
  { label: "5 psig", psig: 5, specificVol: 16.30 },
  { label: "10 psig", psig: 10, specificVol: 11.90 },
  { label: "15 psig", psig: 15, specificVol: 9.40 },
  { label: "25 psig", psig: 25, specificVol: 6.66 },
  { label: "30 psig", psig: 30, specificVol: 5.80 },
  { label: "40 psig", psig: 40, specificVol: 4.67 },
  { label: "50 psig", psig: 50, specificVol: 3.89 },
  { label: "60 psig", psig: 60, specificVol: 3.34 },
  { label: "80 psig", psig: 80, specificVol: 2.60 },
  { label: "100 psig", psig: 100, specificVol: 2.14 },
  { label: "125 psig", psig: 125, specificVol: 1.76 },
  { label: "150 psig", psig: 150, specificVol: 1.49 },
];

const MAX_VELOCITIES = [
  { label: "4,000 ft/min (Supply)", value: 4000 },
  { label: "6,000 ft/min (Supply)", value: 6000 },
  { label: "8,000 ft/min (Supply)", value: 8000 },
  { label: "10,000 ft/min (High P)", value: 10000 },
  { label: "2,000 ft/min (Return)", value: 2000 },
  { label: "3,000 ft/min (Return)", value: 3000 },
];

export const SteamPipeSizingCalculator = () => {
  const [load, setLoad] = useState("");
  const [pressureIdx, setPressureIdx] = useState("0");
  const [maxVelIdx, setMaxVelIdx] = useState("0");

  const steamLoad = parseFloat(load);
  const pressure = STEAM_PRESSURES[parseInt(pressureIdx)];
  const maxVel = MAX_VELOCITIES[parseInt(maxVelIdx)];
  const hasInput = steamLoad > 0;

  const results = useMemo(() => {
    if (!hasInput) return [];
    const Vs = pressure.specificVol;
    // Volume flow = lb/hr × ft³/lb ÷ 60 = CFM
    const cfm = (steamLoad * Vs) / 60;

    return STEAM_PIPE_SIZES.map((pipe) => {
      // Area in ft²
      const areaFt2 = Math.PI * Math.pow(pipe.id / 24, 2); // id in inches → radius in feet
      const velocity = cfm / areaFt2; // ft/min
      return {
        nominal: pipe.nominal,
        id: pipe.id,
        velocity: Math.round(velocity),
        ok: velocity <= maxVel.value,
        capacityLbHr: Math.round((maxVel.value * areaFt2 * 60) / Vs),
      };
    });
  }, [hasInput, steamLoad, pressure, maxVel]);

  const recommended = results.find((r) => r.ok);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Size <span className="text-foreground font-semibold">steam supply and return lines</span> based on steam load, operating pressure, and maximum allowable velocity.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Steam Load (lbs/hr)</label>
          <Input type="number" inputMode="decimal" placeholder="Enter steam load…" value={load} onChange={(e) => setLoad(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Operating Pressure</label>
          <Select value={pressureIdx} onValueChange={setPressureIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>{STEAM_PRESSURES.map((p, i) => (<SelectItem key={i} value={String(i)}>{p.label}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Max Velocity</label>
          <Select value={maxVelIdx} onValueChange={setMaxVelIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>{MAX_VELOCITIES.map((v, i) => (<SelectItem key={i} value={String(i)}>{v.label}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      </div>

      {hasInput && recommended && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Recommended Size" value={recommended.nominal} unit="" accent />
          <ResultCard label="Velocity" value={recommended.velocity.toLocaleString()} unit="ft/min" />
          <ResultCard label="Max Capacity" value={recommended.capacityLbHr.toLocaleString()} unit="lbs/hr" />
          <ResultCard label="Specific Volume" value={fmt(pressure.specificVol, 2)} unit="ft³/lb" />
        </div>
      )}

      {hasInput && results.length > 0 && (
        <div className="mt-4 animate-fade-in">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">All Sizes</h3>
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-muted-foreground font-semibold">Size</th>
                  <th className="text-right px-4 py-2 text-muted-foreground font-semibold">Velocity</th>
                  <th className="text-right px-4 py-2 text-muted-foreground font-semibold">Capacity</th>
                  <th className="text-center px-4 py-2 text-muted-foreground font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.nominal} className={`border-b border-border/50 ${r.ok ? "" : "opacity-50"}`}>
                    <td className="px-4 py-2 font-mono-data font-semibold text-foreground">{r.nominal}</td>
                    <td className="px-4 py-2 font-mono-data text-right text-foreground">{r.velocity.toLocaleString()}</td>
                    <td className="px-4 py-2 font-mono-data text-right text-foreground">{r.capacityLbHr.toLocaleString()}</td>
                    <td className="px-4 py-2 text-center">{r.ok ? <span className="text-primary font-bold">✓</span> : <span className="text-destructive">✗</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
